import React, { useEffect, useRef, useState } from "react";
import Button from "../../components/ui/Button/Button.jsx";
import { money } from "../../utils/format.js";
import { getStoredUser } from "../../services/api.js";
import { downloadServiceFeeReceiptPdf, emailServiceFeeReceipt } from "../../services/paymentDetailsService.js";

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// payment: a row from getServiceFeePaymentHistory() (ServiceFeeHistoryResponse).
// autoSend: opened via "Resend" - auto-focuses the email field.
const PaymentReceiptModal = ({ payment, autoSend, onClose, showToast }) => {
  const user = getStoredUser();
  const [email, setEmail] = useState(user?.email || "");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    if (autoSend && emailRef.current) {
      emailRef.current.focus();
      emailRef.current.select();
    }
  }, [autoSend]);

  if (!payment) return null;

  const handleSend = async () => {
    setEmailError("");
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Email address is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await emailServiceFeeReceipt(payment.id, payment.source, trimmed);
      showToast(`Receipt sent to ${trimmed}`);
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to send receipt");
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await downloadServiceFeeReceiptPdf(payment.id, payment.source);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showToast(err.message || "Could not download receipt");
    } finally {
      setDownloading(false);
    }
  };

  const rows = [
    ["Confirmation #", <span className="font-mono font-semibold text-[var(--accent)]">{payment.confirmationNumber || "—"}</span>],
    ["Description", payment.description || "Form 2290 e-filing service fee"],
    ["Date", fmtDate(payment.date)],
    ["Amount", <span className="font-bold text-[var(--ink)]">{money(payment.amount)}</span>],
    [
      "Status",
      <span
        className="text-[12px] font-bold py-[2px] px-2 rounded-[20px]"
        style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
      >
        {payment.status}
      </span>,
    ],
    ["Payment method", payment.paymentMethod || "—"],
    ["Business", payment.businessName || "—"],
  ];

  return (
    <div
      className="fixed inset-0 z-[800] flex items-center justify-center p-5"
      style={{ background: "rgba(15,23,42,.5)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[var(--panel)] rounded-[20px] w-full max-w-[520px] shadow-[0_24px_64px_rgba(0,0,0,0.3)] overflow-hidden border border-[var(--line)]">
        <div className="py-6 px-6 text-center" style={{ background: "var(--ink)" }}>
          <div className="text-[1.1rem] font-extrabold text-white">EasyFile2290</div>
          <div className="text-[13px] text-white/50 mt-[3px]">Payment Receipt</div>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <div className="text-[11px] font-bold text-[var(--muted)] uppercase tracking-[0.06em] mb-[10px]">
              Receipt Details
            </div>
            {rows.map(([label, val], i) => (
              <div
                key={i}
                className="flex justify-between items-start py-2 border-b border-[var(--line)] last:border-b-0 text-[13px]"
              >
                <span className="text-[var(--muted)] shrink-0">{label}</span>
                <span className="text-[var(--ink)] text-right">{val}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="py-4 px-6 border-t border-[var(--line)] flex flex-col gap-3">
          <div className="text-[13px] font-semibold text-[var(--ink)] mb-1">Send receipt to:</div>
          <div className="flex gap-2">
            <input
              ref={emailRef}
              className="flex-1 border-[1.5px] border-[var(--line-2)] rounded-[8px] py-[9px] px-3 text-[13px] text-[var(--ink)] bg-[var(--panel)] outline-none font-[inherit] focus:border-[var(--accent)]"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
              }}
              placeholder="Email address"
              disabled={sending}
            />
            <Button size="sm" onClick={handleSend} disabled={sending} className="!px-4 !h-auto !py-[9px]">
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
          {emailError && <div className="text-[13px] text-[var(--danger)]">{emailError}</div>}
          <div className="flex gap-2">
            <Button variant="ghost" block onClick={onClose}>
              Close
            </Button>
            <Button variant="ghost" block onClick={handleDownload} disabled={downloading}>
              {downloading ? "Downloading…" : "↓ Download Receipt"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceiptModal;
